"use client";

import { useState, useTransition } from "react";
import {
  clearSquadRollCall,
  saveSquadRollCall,
} from "@/app/actions/companion-people";
import type {
  CompanionPeopleSquad,
  CompanionRollCallEntry,
  SquadCommitmentType,
} from "@/lib/companion/people-loop";

const COMMITMENTS: Array<{ value: SquadCommitmentType; label: string }> = [
  { value: "in", label: "I'm in" },
  { value: "maybe", label: "Maybe" },
  { value: "first_lift", label: "First lift" },
  { value: "dawn_patrol", label: "Dawn patrol" },
  { value: "first_lap", label: "First lap" },
  { value: "first_launch", label: "First launch" },
  { value: "working", label: "Working" },
  { value: "rest_day", label: "Rest day" },
  { value: "pub_later", label: "Pub later" },
];

export function CompanionRollCall({
  entries,
  squads,
  selectedDate,
  selectedSquadId,
  timezone,
  defaultPlace,
  viewerUserId,
}: {
  entries: CompanionRollCallEntry[];
  squads: CompanionPeopleSquad[];
  selectedDate: string;
  selectedSquadId: string | null;
  timezone: string;
  defaultPlace: {
    baseId: string | null;
    slug: string | null;
    name: string;
    sport: string | null;
  } | null;
  viewerUserId: string | null;
}) {
  const [pending, startTransition] = useTransition();
  const [message, setMessage] = useState<string | null>(null);

  function submit(formData: FormData) {
    if (!defaultPlace) return;
    const squadId = String(formData.get("squad") ?? "");
    const commitmentType = String(formData.get("commitment") ?? "in");
    const aimTimeLocal = String(formData.get("aim-time") ?? "").trim() || null;
    startTransition(async () => {
      const result = await saveSquadRollCall({
        squadId,
        targetLocalDate: selectedDate,
        timezone,
        baseId: defaultPlace.baseId,
        placeSlug: defaultPlace.slug,
        placeName: defaultPlace.name,
        sport: defaultPlace.sport,
        commitmentType,
        aimTimeLocal,
        meetupId: null,
        note: null,
      });
      setMessage(result.ok ? "Roll call updated." : result.error);
    });
  }

  function clear(entry: CompanionRollCallEntry) {
    startTransition(async () => {
      const result = await clearSquadRollCall({
        squadId: entry.squadId,
        targetLocalDate: entry.targetLocalDate,
        timezone: entry.timezone,
      });
      setMessage(result.ok ? "Roll call cleared." : result.error);
    });
  }

  return (
    <section aria-labelledby="people-roll-call-heading" className="mt-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[1.4px] text-teal-light">
            Roll call
          </p>
          <h2 id="people-roll-call-heading" className="mt-1 font-serif text-[28px] leading-tight text-white">
            Who&apos;s in
          </h2>
        </div>
        <p className="text-[12px] text-white/60">{entries.length} checked in</p>
      </div>

      {entries.length > 0 ? (
        <ul className="mt-4 divide-y divide-white/10 border-y border-white/10">
          {entries.map((entry) => (
            <li key={entry.id} className="grid gap-1 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(120px,auto)_minmax(140px,auto)_minmax(90px,auto)] sm:items-center sm:gap-4">
              <p className="text-[14px] font-semibold text-white">{entry.displayName}</p>
              <p className="text-[12px] font-semibold text-teal-light">{commitmentLabel(entry.commitmentType)}</p>
              <p className="text-[12px] text-white/75">{entry.placeName}</p>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <p className="text-[12px] tabular-nums text-white/60">{formatTime(entry.aimTimeLocal)}</p>
                {viewerUserId === entry.userId ? (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => clear(entry)}
                    className="inline-flex min-h-[44px] items-center rounded-sm border border-white/20 px-3 text-[11px] font-semibold text-white hover:border-white/40 disabled:opacity-60"
                  >
                    Clear my roll call
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 rounded-sm border border-white/10 bg-white/5 px-3 py-3 text-[13px] text-white/70">
          Nobody has checked in yet. Set the first plan for this Squad.
        </p>
      )}

      {squads.length > 0 && defaultPlace ? (
        <form action={submit} className="mt-4 grid gap-2 rounded-sm border border-white/10 bg-white/5 p-3 sm:grid-cols-[minmax(140px,1fr)_minmax(140px,1fr)_110px_auto] sm:items-end">
          <label className="text-[11px] font-semibold text-white/75">
            Squad
            <select name="squad" defaultValue={selectedSquadId ?? squads[0]?.id} className="mt-1 min-h-[44px] w-full rounded-sm border border-white/15 bg-navy-hover px-3 text-[13px] text-white">
              {squads.map((squad) => <option key={squad.id} value={squad.id}>{squad.name}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-white/75">
            Plan
            <select name="commitment" defaultValue="in" className="mt-1 min-h-[44px] w-full rounded-sm border border-white/15 bg-navy-hover px-3 text-[13px] text-white">
              {COMMITMENTS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-white/75">
            Aim time
            <input name="aim-time" type="time" className="mt-1 min-h-[44px] w-full rounded-sm border border-white/15 bg-navy-hover px-3 text-[13px] text-white" />
          </label>
          <button type="submit" disabled={pending} className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-teal px-4 text-[13px] font-semibold text-white hover:bg-teal-dark disabled:cursor-wait disabled:opacity-60">
            {pending ? "Updating..." : "Update my roll call"}
          </button>
        </form>
      ) : (
        <p className="mt-4 text-[12px] text-white/60">
          Save a place and join a Squad to start the roll call.
        </p>
      )}
      <p aria-live="polite" className="mt-2 min-h-5 text-[12px] text-white/75">{message}</p>
    </section>
  );
}

function commitmentLabel(value: SquadCommitmentType): string {
  return COMMITMENTS.find((item) => item.value === value)?.label ?? value.replaceAll("_", " ");
}

function formatTime(value: string | null): string {
  return value ? value.slice(0, 5) : "Time open";
}
